import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Home, Building, Key, HardHat, Sparkles, Square, ChefHat,
  ShieldCheck, Eye, Calendar, BadgeDollarSign, MapPin,
  Phone, Mail, Star, ArrowRight, CheckCircle2
} from 'lucide-react';
import { mockServices, mockTestimonials, mockWhyChooseUs } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  home: Home,
  building: Building,
  key: Key,
  'hard-hat': HardHat,
  sparkles: Sparkles,
  square: Square,
  'chef-hat': ChefHat,
  'shield-check': ShieldCheck,
  eye: Eye,
  calendar: Calendar,
  'badge-dollar-sign': BadgeDollarSign,
  'map-pin': MapPin
};

const HomePage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    cleaningType: '',
    additionalDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/quotes`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          propertyType: '',
          bedrooms: '',
          bathrooms: '',
          cleaningType: '',
          additionalDetails: ''
        });
      } else {
        toast.error(response.data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error('Unable to submit request. Please call us directly at 0225896143.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToQuote = () => {
    document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center">
              <a href="/" className="flex flex-col">
                <span className="text-3xl font-black text-gray-900 tracking-tight leading-none">
                  THE CLEANING CREW
                </span>
                <span className="text-sm font-semibold text-red-600 tracking-wider uppercase mt-1">
                  Whakatane
                </span>
              </a>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-red-600 transition-colors font-semibold text-lg">Home</a>
              <a href="/blog" className="text-gray-700 hover:text-red-600 transition-colors font-semibold text-lg">Blog</a>
              <a href="tel:0225896143" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-semibold text-lg">0225896143</span>
              </a>
              <Button onClick={scrollToQuote} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-6 text-lg shadow-lg">
                Get a Quote
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20 min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.5), rgba(0,0,0,0.3)), url(https://images.unsplash.com/photo-1581578731548-c64695cc6952)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl py-20">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Cleaner When the Crew Shows Up!
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed max-w-2xl">
              Professional cleaning for homes and businesses across Whakatane. We turn up, do it properly, and deliver the best cleaning experience every time. No dramas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a href="tel:0225896143">
                <Button size="lg" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 font-semibold">
                  <Phone className="w-5 h-5 mr-2" />
                  0225896143
                </Button>
              </a>
              <Button 
                onClick={scrollToQuote}
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 font-semibold"
              >
                Get Free Quote
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-8 text-white">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-6 py-4 rounded-xl border border-white/20">
                <div className="flex">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />)}
                </div>
                <div className="text-left">
                  <div className="font-bold text-2xl">5.0</div>
                  <div className="text-sm text-white/90">Google Reviews</div>
                </div>
              </div>
              <div className="h-12 w-px bg-white/30" />
              <div className="text-left">
                <div className="font-bold text-3xl">900+</div>
                <div className="text-sm text-white/90">Jobs completed</div>
              </div>
              <div className="h-12 w-px bg-white/30" />
              <div className="text-left">
                <div className="font-bold text-3xl">100%</div>
                <div className="text-sm text-white/90">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-blue-600 font-semibold text-lg mb-4">#WhakataneLegends</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Look at us go.
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Cleaning Crew is your local cleaning business dedicated to providing exceptional service to Whakatane and surrounding areas.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  We don't just clean — we deliver peace of mind. From homes to offices, we treat every space with care and professionalism. No shortcuts, no dramas, just spotless results every time.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Basically, we clean, maintain, and quietly make your space better than it was. Not our words, but we'll take it.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-2">7+</div>
                  <div className="text-gray-700 font-semibold">Service Types</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-5xl font-bold bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent mb-2">100+</div>
                  <div className="text-gray-700 font-semibold">Happy Clients</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent mb-2">900+</div>
                  <div className="text-gray-700 font-semibold">Jobs Done</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-8 rounded-2xl text-center shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-5xl font-bold bg-gradient-to-r from-yellow-600 to-yellow-800 bg-clip-text text-transparent mb-2">5.0★</div>
                  <div className="text-gray-700 font-semibold">Google Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-semibold text-lg mb-4">Our core services</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              We clean. We maintain. We care.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to keep your space spotless
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {mockServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id}
                  className="group overflow-hidden border-0 bg-white shadow-xl hover:shadow-premium-lg transition-all duration-500 transform hover:scale-105"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-white text-sm font-bold border border-white/30 inline-block">
                            0{index + 1}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl text-gray-900 font-bold group-hover:text-red-600 transition-colors">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 translate-y-1/2" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Why Choose The Cleaning Crew
            </h2>
            <p className="text-lg text-white/90">
              Excellence in every detail, every time
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockWhyChooseUs.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div 
                  key={item.id}
                  className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-300 hover:scale-105 hover:shadow-premium-lg"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-white/80 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-semibold text-lg mb-4">Hundreds trust The Cleaning Crew</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Don't take our word for it
            </h2>
            <p className="text-lg text-gray-600">
              See what our clients say about working with us
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4 italic">"{testimonial.comment}"</p>
                  <div className="mt-4 pt-4 border-t">
                    <CardTitle className="text-lg font-bold text-gray-900">{testimonial.name}</CardTitle>
                    <CardDescription className="text-sm">{testimonial.location}</CardDescription>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Tell us what you need. We'll sort it.
              </h2>
              <p className="text-lg text-gray-600">
                Quick form, straight answer. Give us the basics and we'll come back with a number and a crew that turns up.
              </p>
            </div>
            
            <Card className="border-2">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input 
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        placeholder="John Smith"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input 
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="021 234 5678"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="propertyType">Property Type *</Label>
                      <Select 
                        value={formData.propertyType}
                        onValueChange={(value) => handleSelectChange('propertyType', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="house">House</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="rental">Rental</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cleaningType">Type of Cleaning Needed *</Label>
                      <Select 
                        value={formData.cleaningType}
                        onValueChange={(value) => handleSelectChange('cleaningType', value)}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select cleaning type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="domestic">Domestic Cleaning</SelectItem>
                          <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                          <SelectItem value="end-of-tenancy">End of Tenancy</SelectItem>
                          <SelectItem value="builders">Builders Cleaning</SelectItem>
                          <SelectItem value="deep">Deep Cleaning</SelectItem>
                          <SelectItem value="windows">Window Cleaning</SelectItem>
                          <SelectItem value="oven">Oven & Kitchen Deep Cleaning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="bedrooms">Number of Bedrooms</Label>
                      <Input 
                        id="bedrooms"
                        name="bedrooms"
                        type="number"
                        min="0"
                        value={formData.bedrooms}
                        onChange={handleInputChange}
                        placeholder="3"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bathrooms">Number of Bathrooms</Label>
                      <Input 
                        id="bathrooms"
                        name="bathrooms"
                        type="number"
                        min="0"
                        value={formData.bathrooms}
                        onChange={handleInputChange}
                        placeholder="2"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="additionalDetails">Additional Details</Label>
                    <Textarea 
                      id="additionalDetails"
                      name="additionalDetails"
                      value={formData.additionalDetails}
                      onChange={handleInputChange}
                      placeholder="Tell us more about your cleaning needs..."
                      rows={4}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl animate-pulse-subtle" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500 rounded-full filter blur-3xl animate-pulse-subtle" style={{animationDelay: '1s'}} />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Ready for a Spotless Space?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Get in touch today for a free, no-obligation quote. Experience the difference of true professional cleaning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0225896143">
                <Button size="lg" className="w-full sm:w-auto bg-white text-gray-900 hover:bg-gray-100 text-lg px-10 py-7 font-bold shadow-premium-lg hover:shadow-premium transform hover:scale-105 transition-all">
                  <Phone className="w-6 h-6 mr-2" />
                  Call 0225896143
                </Button>
              </a>
              <Button 
                onClick={scrollToQuote}
                size="lg" 
                className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg px-10 py-7 font-bold shadow-premium-lg hover:shadow-premium transform hover:scale-105 transition-all border-2 border-white/20"
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex flex-col mb-4">
                <span className="text-2xl font-black text-white tracking-tight leading-none">
                  THE CLEANING CREW
                </span>
                <span className="text-sm font-semibold text-red-400 tracking-wider uppercase mt-2">
                  Whakatane
                </span>
              </div>
              <p className="text-gray-400">
                Premium cleaning services in Whakatane and surrounding areas.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Contact Us</h4>
              <div className="space-y-2">
                <a href="tel:0225896143" className="flex items-center space-x-2 hover:text-blue-400 transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>0225896143</span>
                </a>
                <a href="mailto:ajit@the-cleaning-crew.org" className="flex items-center space-x-2 hover:text-blue-400 transition-colors">
                  <Mail className="w-4 h-4" />
                  <span>ajit@the-cleaning-crew.org</span>
                </a>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Whakatane, New Zealand</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Our Services</h4>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  <span>Domestic Cleaning</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  <span>Commercial Cleaning</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  <span>End of Tenancy Cleaning</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400" />
                  <span>Deep Cleaning</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 The Cleaning Crew. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
